import React, { useState, useEffect } from "react";
import { PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { setApplePayAvailible } from "../../store/reducers/payment";
import { API_URL, TRIAL_DAYS, STRIPE_PRICE_ID } from "../../constants/formConstants";
import axios from "axios";
import { getUtmMetadataFromSearch } from "../../utils/utm";
import { pushPurchaseEvent } from "../../utils/gtm";
import { sendAmplitudePurchase } from "../../utils/amplitude";

const AppleGooglePayButton = ({ trialCost, onSuccess, isDisabled = false }) => {
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestKey, setPaymentRequestKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const stripe = useStripe();
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(window.location.search);
  const clientReferenceId = searchParams.get("client_reference_id");
  const hostname = window.location?.hostname || "";
  const pathname = window.location?.pathname || "";
  const paywallMap = [
    // Canonical new routes
    { prefix: "/colortype-3month-29-upsell-v2", label: "colortype-3month-29-upsell" },
    { prefix: "/colortype-3month-29-upsell", label: "colortype-3month-29-upsell" },
    { prefix: "/colortype-3month-29", label: "colortype-3month-29" },
    { prefix: "/colortype-1month-29", label: "colortype-1month-29" },
    { prefix: "/haircolor-3month-29", label: "haircolor-3month-29" },
    { prefix: "/hairstyle-3month-29-man", label: "hairstyle-3month-29-man" },
    { prefix: "/hairstyle-3month-29-woman", label: "hairstyle-3month-29-woman" },
    { prefix: "/bodytype-3month-29-woman", label: "bodytype-3month-29-woman" },
    { prefix: "/faceshape-3month-29", label: "faceshape-3month-29" },
    { prefix: "/faceshape_3month_29", label: "faceshape-3month-29" },
    // Legacy routes mapped to new labels
    { prefix: "/v1_3months_upsell", label: "colortype-3month-29-upsell" },
    { prefix: "/v1", label: "colortype-3month-29" },
    { prefix: "/month", label: "colortype-1month-29" },
    { prefix: "/hair", label: "haircolor-3month-29" },
    { prefix: "/hairstyle-man", label: "hairstyle-3month-29-man" },
    { prefix: "/hairstyle", label: "hairstyle-3month-29-woman" },
    // Other legacy
    { prefix: "/v2", label: "v2" },
  ];

  const matchedPaywall =
    paywallMap.find((item) => pathname.startsWith(item.prefix))?.label || undefined;

  const isMovereliefRoot =
    pathname === "/" && ["moverelief.pro", "www.moverelief.pro"].includes(hostname);
  const paywall = isMovereliefRoot
    ? "faceshape-3month-29"
    : pathname === "/"
      ? "main"
      : matchedPaywall;
  const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 47 });
  const persistTrackingContext = () => {
    try {
      if (paywall) {
        sessionStorage.setItem("lastPaywall", paywall);
      }
      if (clientReferenceId) {
        sessionStorage.setItem("clientReferenceId", clientReferenceId);
      }
    } catch (_) {}
  };

  const options = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        type: "default",
        theme: "dark",
        height: "55px",
      },
    },
  };

  useEffect(() => {
    if (!stripe || !trialCost) return;

    const amount = Math.round(Number(trialCost) * 100);
    if (!amount || amount <= 0) return;

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Get your color",
        amount,
      },
      requestPayerEmail: true,
      requestPayerName: true,
    });

    pr.canMakePayment().then((res) => {
      if (res) {
        dispatch(setApplePayAvailible(true));
        setPaymentRequest(pr);
        setPaymentRequestKey((prev) => prev + 1);
      } else {
        dispatch(setApplePayAvailible(false));
      }
    });

    pr.on("paymentmethod", async (e) => {
      setLoading(true);
      setErrorMessage(""); // Clear previous errors
      const now = new Date();
      now.setDate(now.getDate() + TRIAL_DAYS);
      const trialEnd = Math.floor(now.valueOf() / 1000);

      // SUBSCRIPTION CREATION DISABLED - Use payment-only endpoint
      let existingCustomerId = null;
      try {
        existingCustomerId = sessionStorage.getItem("stripeCustomerId");
      } catch {}

      const bodyObj = {
        amount,
        id: e.paymentMethod.id,
        email: e.payerEmail || "applegoogle@email.com",
        data: {
          currency: "usd",
          client_reference_id: clientReferenceId,
          ...utmMetadata,
          ...(paywall ? { paywall } : {}),
        },
        ...(existingCustomerId ? { customerId: existingCustomerId } : {}),
      };

      try {
        // Use payment endpoint instead of create-payment-intent to avoid subscriptions
        const idempotencyKey = `${e.paymentMethod.id}-${amount}`;
        const response = await axios.post(`${API_URL}/payment`, bodyObj, {
          headers: { "Idempotency-Key": idempotencyKey },
        });
        const returnedCustomerId =
          response?.data?.customerId || response?.data?.customer?.id;
        if (returnedCustomerId) {
          try {
            sessionStorage.setItem("stripeCustomerId", returnedCustomerId);
          } catch {}
        }
        
        if (response.data.success) {
          try {
            sessionStorage.setItem("stripePaymentMethodId", e.paymentMethod.id);
          } catch {}
          e.complete("success");
          setLoading(false);
          persistTrackingContext();
          await pushPurchaseEvent({
            clientReferenceId,
            paywall,
            value: Number(trialCost),
            type: "trial",
          });
          await sendAmplitudePurchase({
            userId: clientReferenceId,
            revenue: Number(trialCost),
            currency: "usd",
            product: "Trial payment",
            paywall,
            type: "trial",
          });
          if (onSuccess) onSuccess();
        } else if (response.data.requires_action) {
          // Close wallet sheet before starting 3DS
          e.complete("success");
          try {
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
              response.data.client_secret
            );
            if (confirmError) {
              setLoading(false);
              setErrorMessage(confirmError.message);
            } else {
              const status = paymentIntent && paymentIntent.status;
              if (
                status === "succeeded" ||
                status === "processing" ||
                status === "requires_capture"
              ) {
                try {
                  sessionStorage.setItem("stripePaymentMethodId", e.paymentMethod.id);
                } catch {}
                setLoading(false);
                persistTrackingContext();
                await pushPurchaseEvent({
                  clientReferenceId,
                  paywall,
                  value: Number(trialCost),
                  type: "trial",
                });
                await sendAmplitudePurchase({
                  userId: clientReferenceId,
                  revenue: Number(trialCost),
                  currency: "usd",
                  product: "Trial payment",
                  paywall,
                  type: "trial",
                });
                if (onSuccess) onSuccess();
              } else {
                setLoading(false);
                setErrorMessage(
                  "Payment authentication failed. Please try again."
                );
              }
            }
          } catch (err) {
            setLoading(false);
            setErrorMessage("Payment authentication failed. Please try again.");
          }
        } else {
          e.complete("fail");
          setLoading(false);
          setErrorMessage(response.data.message || "Payment failed. Please try again.");
        }
      } catch (error) {
        console.log("Apple Pay error", error);
        e.complete("fail");
        setLoading(false);
        setErrorMessage("Payment failed. Please try again.");
      }
    });
  }, [stripe, trialCost]);

  if (!paymentRequest) return null;

  return (
    <div
      className={`mb-4 ${isDisabled ? "opacity-50 pointer-events-none" : ""} ${
        loading ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div
        key={paymentRequestKey}
        style={{
          marginBottom: 8,
          overflow: "hidden",
          borderRadius: 8,
        }}
      >
        <PaymentRequestButtonElement
          options={options}
          className="apple-google-pay"
        />
      </div>
      {errorMessage && (
        <div className="mt-2 text-red-600 text-sm text-center">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default AppleGooglePayButton;
